<script>
    import { createEventDispatcher } from "svelte";
    import { DoubleBounce } from "svelte-loading-spinners";
    import Select from "svelte-select/Select.svelte";
    import EventFiles from "../../../Components/Common/EventFiles.svelte";
    import {
        DateInput,
        NotificationModal,
    } from "../../../Components/Common/index.svelte";
    import NotesTableWithEdit from "../../../Components/Common/NotesTableWithEdit.svelte";

    import { Notes } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        deleteIcon,
        editIcon,
        infoIcon,
        NoteTypes,
    } from "../../../Utils/utilConstants.svelte";

    import {
        accountCalendarEventType,
        EditRepeatingEventOptions,
        DeleteRepeatingEventOptions,
        DirectEncounterStatus,
    } from "../../../Utils/utilEnums.svelte";

    import {
        getBackEndDeleteType,
        getEncounterDischargeDateWithFallBackDuration,
        getOptionLabelForSurgeon,
        getTimeDiffInMillSec,
        showError,
    } from "../../../Utils/utilFunctions.svelte";
    import CalendarRecurrenceEditOptionModal from "../../Calendar/components/CalendarRecurrenceEditOptionModal.svelte";

    import CalendarRecurrenceModal from "../../Calendar/components/CalendarRecurrenceModal.svelte";
    import {
        getTimeAfterChanges,
        resetCalendarRecurrenceEvent,
        _isAllDayEvent,
    } from "../../Calendar/helpers/calendarHelper.svelte";
    import CalendarEventCategorySelector from "../../CalendarCategories/CalendarEventCategorySelector.svelte";
    import CreateSurgeonModal from "../../PatientEncounter/components/CreateSurgeonModal.svelte";
    import { getMatchedSurgeon } from "../../PatientEncounter/helpers/encounterHelper.svelte";
    import CalendarEventMetadata from "../../Calendar/components/CalendarEventMetadata.svelte";

    export let id,
        nestedEncounter = [];
    export let newValues = {};
    export let isModalOpen = false;
    export let isOpenFromCalendar = false;
    export let closeModal = () => {};
    export let afterSubmit = () => {};

    $: isCreateEvent = !accountEventObj.hasOwnProperty("id");

    $: _syncSurgeonOptions(accountDoctors, suggestedAccountDoctors);

    $: if (!isModalOpen) clearAll();

    let isModifyingEvent = false;

    let isCalendarRecurrenceModalOpen = false;

    let isDeleteEventModalOpen = false,
        accountEventObj = {},
        prevStartDate,
        allFiles = [],
        isLoadingEvent = false,
        isDeletingEvent = false;

    let deleteType, wasEventRepeating;

    const dispatch = createEventDispatcher();

    const init = async () => {
        if (id) {
            isLoadingEvent = true;
            accountEventObj = await apiMethods.getMethod
                .getEventById(id)
                .catch((err) => {
                    showError(err);
                    closeModal();
                });
            // for nested encounters time adjustment
            prevStartDate = accountEventObj.fromDate;
            allFiles = accountEventObj.eventFiles;
            accountEventObj = {
                ...accountEventObj,
                fromTime: accountEventObj.fromDate.split("T")[1].slice(0, 5),
                toTime: accountEventObj.toDate.split("T")[1].slice(0, 5),
                isRepeating:
                    (accountEventObj.eventType == 1 ||
                        accountEventObj.eventType == 2) &&
                    accountEventObj.repeatIntervalType > 0,
                isFullDay: _isAllDayEvent(
                    accountEventObj.fromDate,
                    accountEventObj.toDate,
                ),
                ...newValues,
            };
            wasEventRepeating = accountEventObj.isRepeating;
            isLoadingEvent = false;
        } else {
            accountEventObj = {
                ...newValues,
            };
        }
    };

    $: title = isLoadingEvent
        ? "Loading"
        : isCreateEvent
          ? "Create Event"
          : "Edit Event";

    const handleModifyEvent = async (e) => {
        const editType = e?.detail;
        if (isModifyingEvent) return;

        isModifyingEvent = true;

        _syncDateToCalendarEvent();

        if (accountEventObj.eventType === 1) {
            accountEventObj.hospitalId = selectedHospital?.id ?? 0;
            accountEventObj.surgeonId = selectedSurgeon?.id ?? 0;
        }

        if (
            accountEventObj.eventType == 0 ||
            (accountEventObj.hasOwnProperty("isRepeating") &&
                !accountEventObj.isRepeating)
        )
            accountEventObj = resetCalendarRecurrenceEvent(accountEventObj);

        if (isCreateEvent) await createEvent();
        else if (accountEventObj.eventType == 1 && nestedEncounter?.length)
            return updateListAndNestedEncounter(editType);
        else await updateEvent(editType);

        isModifyingEvent = false;
    };

    const clearAll = () => {
        accountEventObj = {};
        isLoadingEvent = false;
        wasEventRepeating = false;
        prevStartDate = null;
        newValues = {};
        selectedHospital = null;
        selectedSurgeon = null;
        surgeonOptions = [];
        hospitalOptions = [];
        suggestedAccountDoctors = [];
        suggestedHospitals = [];
        accountDoctors = [];
        isCreateNewSurgeonModalOpen = false;
        isCalendarRecurrenceEditOptionModalOpen = false;
        isLoadingSuggestedAccountDoctors = false;
        isLoadingSuggestedHospitals = false;
    };

    const onConfirmModifyEvent = () => {
        if (
            (accountEventObj.isRepeating || wasEventRepeating) &&
            !isCreateEvent
        ) {
            return (isCalendarRecurrenceEditOptionModalOpen = true);
        } else handleModifyEvent();
    };

    const updateListAndNestedEncounter = async (editType) => {
        const allCalls = [];
        const newStartDate = `${accountEventObj.fromDate}`;
        const timeDiff = getTimeDiffInMillSec(newStartDate, prevStartDate);

        if (timeDiff !== 0) {
            nestedEncounter.forEach((encounter) => {
                const newDate = getTimeAfterChanges(
                    encounter.scheduledDate,
                    timeDiff,
                );
                const dischargeDate = encounter.dischargeDate
                    ? getTimeAfterChanges(
                          getEncounterDischargeDateWithFallBackDuration(
                              encounter,
                          ),
                          timeDiff,
                      )
                    : null;
                allCalls.push(
                    apiMethods.putMethod
                        .updateEncounterScheduleDate(encounter.id, {
                            scheduledDate: newDate,
                            dischargeDate,
                        })
                        .then((res) => {
                            if (!res || res.error || !res.id) {
                                const err =
                                    res.error ??
                                    "Failed to update encounter date/time, please refresh the page and try again and contact support if the issue persists.";
                                throw new Error(err);
                            }
                        })
                        .catch(() => {
                            showError(
                                "Failed to update some encounters, please refresh the page and try again and contact support if the issue persists.",
                            );
                        }),
                );
            });
        }

        await updateEvent(editType);
        await Promise.all(allCalls);
        isModifyingEvent = false;

        afterSubmit();
    };

    const createEvent = async () => {
        await apiMethods.postMethod
            .createAccountCalendarEvent(accountEventObj)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to create calendar event, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    closeModal();

                    afterSubmit();
                }
            })
            .catch((err) => showError(err));
    };

    const updateEvent = async (editType) => {
        if (accountEventObj.isRepeating || wasEventRepeating) {
            switch (editType) {
                case EditRepeatingEventOptions.thisOnly:
                    accountEventObj =
                        resetCalendarRecurrenceEvent(accountEventObj);
                    break;
                case EditRepeatingEventOptions.thisAndFollowing:
                    accountEventObj.parentId = accountEventObj.id;
                    break;
                default:
                    break;
            }
        }
        await apiMethods.putMethod
            .updateAccountCalendarEvent(accountEventObj)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update event, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    closeModal();

                    afterSubmit();
                }
            })
            .catch((err) => showError(err));
    };

    const deleteEvent = async () => {
        isDeletingEvent = true;

        await apiMethods.deleteMethod
            .deleteAccountCalendarEvent(
                accountEventObj.id,
                getBackEndDeleteType(deleteType),
            )
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to delete event, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    closeModal();
                    afterSubmit();
                    dispatch("eventDeleted");
                }
            })
            .catch((err) => showError(err));

        isDeletingEvent = false;

        closeDeleteEventModal();
    };

    const openRecurrenceModalOpen = () =>
        (isCalendarRecurrenceModalOpen = true);

    const openDeleteEventModal = () => (isDeleteEventModalOpen = true);

    const closeDeleteEventModal = () => {
        deleteType = undefined;
        isDeleteEventModalOpen = false;
    };

    const handleDateChange = (obj, isFromDate = true) => {
        if (obj.isFullDay) return obj;

        if (isFromDate && obj.fromTime) return obj;

        if (!isFromDate && obj.toTime) return obj;

        if (isFromDate) obj.fromTime = "09:00";
        else obj.toTime = "17:00";

        return obj;
    };

    const handleToggleFullDay = (obj) => {
        delete obj.fromTime;
        delete obj.toTime;

        return obj;
    };

    // HELPER FUNCTIONS
    const _syncDateToCalendarEvent = () => {
        accountEventObj.fromDate += accountEventObj.fromTime
            ? `T${accountEventObj.fromTime}:00`
            : "T00:00:00";
        accountEventObj.toDate += accountEventObj.toTime
            ? `T${accountEventObj.toTime}:59`
            : "T23:59:59";
    };

    let selectedHospital,
        selectedSurgeon,
        surgeonOptions = [],
        hospitalOptions = [],
        suggestedAccountDoctors = [],
        suggestedHospitals = [],
        accountDoctors = [],
        isCreateNewSurgeonModalOpen = false,
        isCalendarRecurrenceEditOptionModalOpen = false,
        isLoadingSuggestedAccountDoctors = false,
        isLoadingSuggestedHospitals = false;

    const loadHospitals = async (searchText) => {
        const res = searchText
            ? await apiMethods.getMethod.getHospitals(0, 999999, {
                  search: searchText,
              })
            : [];

        return _handleSearchHospitals(
            res.results?.length > 0 ? res.results : [],
        );
    };

    const _handleSearchHospitals = (res = []) => {
        hospitalOptions = [];

        if (!res.length && !suggestedHospitals.length) return [];

        const suggestedOptions = [];

        if (res.length) {
            hospitalOptions = [...res];
            return [...res];
        }

        if (!suggestedHospitals.length) return [];

        suggestedHospitals.map((hospital) => {
            suggestedOptions.push({
                ...hospital,
                groupName: "Suggested Hospitals",
            });
        });

        hospitalOptions = [...suggestedOptions];

        return [...suggestedOptions];
    };

    const groupBy = (option) => option.groupName;

    const loadSuggestedAccountDoctor = async () => {
        const doctorID = accountEventObj.doctorId;

        if (!doctorID) return;

        isLoadingSuggestedAccountDoctors = true;

        await apiMethods.getMethod.getSuggestedSurgeon(doctorID).then((res) => {
            if (res.results) suggestedAccountDoctors = [...res.results];
        });

        //filter surgeons and take only once by unique id
        suggestedAccountDoctors = [
            ...new Map(suggestedAccountDoctors.map(doc => [doc.providerUniqueId, doc])).values()
        ];

        if (suggestedAccountDoctors.length > 5)
            suggestedAccountDoctors = suggestedAccountDoctors.slice(0, 5);

        await loadAccountDoctors(doctorID);

        isLoadingSuggestedAccountDoctors = false;
    };

    const mapSelectionFromEventData = () => {
        selectedHospital = null;
        selectedSurgeon = null;

        if (!accountEventObj.doctorId) return;

        if (accountEventObj.hospitalId && accountEventObj.hospitalName) {
            selectedHospital = {
                id: accountEventObj.hospitalId,
                name: accountEventObj.hospitalName,
            };
        } else {
            selectedHospital = null;
        }
        if (accountEventObj.surgeonId && accountEventObj.surgeonName) {
            selectedSurgeon = {
                ...selectedSurgeon,
                id: accountEventObj.surgeonId,
                displayLabel: accountEventObj.surgeonName,
            };
        } else {
            selectedSurgeon = null;
        }
    };

    const loadSuggestedHospitals = async () => {
        const doctorID = accountEventObj.doctorId;

        if (!doctorID) return;

        isLoadingSuggestedHospitals = true;

        await apiMethods.getMethod
            .getSuggestedHospital(doctorID)
            .then((res) => {
                if (res.results) {
                    suggestedHospitals = [...res.results];
                    _handleSearchHospitals([]);
                }
            });

        isLoadingSuggestedHospitals = false;
    };

    const loadAccountDoctors = async (id, replaceSelectedSurgeon = true) => {
        await apiMethods.getMethod
            .getAccountDoctors(0, 999999, {
                "filter[doctorId]": id,
            })
            .then((res) => {
                if (res.results) {
                    accountDoctors = [...res.results];

                    // in order to get surgeon uniqueId
                    if (
                        replaceSelectedSurgeon &&
                        surgeonOptions.length &&
                        selectedSurgeon?.id
                    ) {
                        selectedSurgeon = accountDoctors.find(
                            (s) => s.id == accountEventObj.surgeonId,
                        );
                        if (selectedSurgeon?.id)
                            selectedSurgeon.matchingId = `${selectedSurgeon.id}${selectedSurgeon.state}`;
                    }
                } else accountDoctors = [];
            });
    };

    const _syncSurgeonOptions = (
        accountDoctors = [],
        suggestedSurgeons = [],
    ) => {
        surgeonOptions = [];

        let uniqueAccountDoctors = [
            ...new Map(accountDoctors.map(doc => [doc.providerUniqueId, doc])).values()
        ];

        if (!suggestedSurgeons)
            uniqueAccountDoctors.map((doctor) => {
                surgeonOptions.push({
                    ...doctor,
                    displayLabel: getOptionLabelForSurgeon(doctor),
                    matchingId: `${doctor.id}${doctor.state}`,
                });
            });
        else {
            const suggestedSurgeonUniqueIds = [];
            const len = Math.min(suggestedSurgeons.length, 5);

            for (let i = 0; i < len; i++) {
                const surgeon = suggestedSurgeons[i];

                surgeonOptions.push({
                    ...surgeon,
                    groupName: "Suggested surgeons",
                    displayLabel: getOptionLabelForSurgeon(surgeon),
                    matchingId: `${surgeon.id}${surgeon.state}`,
                });

                suggestedSurgeonUniqueIds.push(surgeon.providerUniqueId);
            }

            uniqueAccountDoctors.map((surgeon) => {
                if (
                    !suggestedSurgeonUniqueIds.includes(
                        surgeon.providerUniqueId.toString(),
                    )
                )
                    surgeonOptions.push({
                        ...surgeon,
                        groupName: "Other Surgeons",
                        displayLabel: getOptionLabelForSurgeon(surgeon),
                        matchingId: `${surgeon.id}${surgeon.state}`,
                    });
            });
        }

        selectedSurgeon = getMatchedSurgeon(
            selectedSurgeon,
            { state: accountEventObj.hospitalState },
            {},
            {},
            surgeonOptions,
            nestedEncounter,
        );
    };

    const openCreateNewSurgeonModal = () => {
        isCreateNewSurgeonModalOpen = true;
    };

    const closeCreateNewSurgeonModal = () =>
        (isCreateNewSurgeonModalOpen = false);

    const clearHospital = () => {
        console.log("clear hospital");
        selectedHospital = {};
    };

    const clearSurgeon = () => {
        console.log("clear surgeon");
        selectedSurgeon = {};
    };
</script>

<NotificationModal
    {isModalOpen}
    {title}
    {closeModal}
    modalMaxWidth="max-w-[70vw] relative"
    fullWidthOnSmallerScreens
    noContentPadding
    closeModalFunction={closeModal}
    submitText={`${isCreateEvent ? (isModifyingEvent ? "Creating" : "Create") : isModifyingEvent ? "Saving" : "Save"}`}
    isConfirming={isModifyingEvent}
    confirmAction={onConfirmModifyEvent}
    zIndex="1000"
    headerOptions={[
        {
            optionText: "Info",
            tooltipContent: {
                component: CalendarEventMetadata,
                props: {
                    event: accountEventObj
                }
            },
            iFont: infoIcon,
            iconName: "infoIcon",
            function: () => {
            },
            showAsTooltip: true,
        },
        {
            iconName: "deleteIcon text-sm",
            iFont: deleteIcon,
            optionText: "Delete",
            showAsTooltip: true,
            function: () => openDeleteEventModal(),
            isHidden: !isOpenFromCalendar,
        },
    ]}
>
    <div class="flex flex-1 flex-col gap-y-4 px-6 py-3 overflow-scroll" use:init>
        {#if isLoadingEvent}
            <div class="flex flex-1 my-6 items-center justify-center">
                <DoubleBounce
                    size="32"
                    color="#FF3E00"
                    unit="px"
                    duration="1s"
                />
            </div>
        {:else}
            <div class="flex flex-col gap-y-4 flex-1 py-3">
                <div class="flex flex-row justify-start gap-4 flex-wrap">
                    <div class="flex gap-x-4 items-center">
                        <p class="w-[120px]">Event Type:</p>

                        <select
                            class="select select-sm select-bordered w-[268px]"
                            bind:value={accountEventObj.eventType}
                        >
                            {#each accountCalendarEventType.sort((a, b) => a.order - b.order) as aceType}
                                {#if aceType.creatableFromCalendar}
                                    <option value={aceType.id}
                                        >{aceType.type}</option
                                    >
                                {/if}
                            {/each}
                        </select>
                    </div>

                    <div class="flex gap-x-4 items-center">
                        <p class="w-[120px]">Event category:</p>

                        <CalendarEventCategorySelector
                            bind:value={accountEventObj.eventCategory}
                            widthClass="w-[268px]"
                        />
                    </div>
                </div>

                <div class="flex flex-row justify-start gap-4 flex-wrap">
                    <div class="flex gap-x-4 items-center">
                        <p class="w-[120px]">From Date:</p>

                        <div class="flex flex-1 gap-x-2 items-center w-[268px]">
                            <DateInput
                                bind:value={accountEventObj.fromDate}
                                wrapperClass="input input-sm px-0 input-bordered flex-1"
                                wrapperClassFocus="inputFocus"
                                on:change={() =>
                                    (accountEventObj =
                                        handleDateChange(accountEventObj))}
                            />

                            {#if !accountEventObj.isFullDay}
                                <input
                                    type="time"
                                    bind:value={accountEventObj.fromTime}
                                    class="input input-sm input-bordered"
                                />
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-4 items-center">
                        <p class="w-[120px]">To Date:</p>

                        <div class="flex flex-1 gap-x-2 items-center w-[268px]">
                            <DateInput
                                bind:value={accountEventObj.toDate}
                                wrapperClass="input input-sm px-0 input-bordered flex-1"
                                wrapperClassFocus="inputFocus"
                                on:change={() =>
                                    (accountEventObj = handleDateChange(
                                        accountEventObj,
                                        false,
                                    ))}
                            />

                            {#if !accountEventObj.isFullDay}
                                <input
                                    type="time"
                                    class="input input-sm input-bordered"
                                    bind:value={accountEventObj.toTime}
                                />
                            {/if}
                        </div>
                    </div>
                </div>

                <div class="flex flex-row justify-start gap-4 flex-wrap">
                    <div class="flex gap-x-4 items-center">
                        <label class="w-[120px]" for="fullDay">Full Day:</label>

                        <div class="w-[268px]">
                            <input
                                type="checkbox"
                                id="fullDay"
                                class="toggle toggle-secondary"
                                bind:checked={accountEventObj.isFullDay}
                                on:change={() =>
                                    (accountEventObj =
                                        handleToggleFullDay(accountEventObj))}
                            />
                        </div>
                    </div>
                    {#if accountEventObj.eventType == 1 || accountEventObj.eventType == 2}
                        <div class="flex gap-x-4 items-center">
                            <p class="w-[120px]">Repeating:</p>

                            <div
                                class="flex flex-1 items-center gap-x-6 justify-between w-[268px]"
                            >
                                <input
                                    type="checkbox"
                                    class="toggle toggle-secondary"
                                    bind:checked={accountEventObj.isRepeating}
                                    on:change={() => {
                                        accountEventObj.isRepeating &&
                                            openRecurrenceModalOpen();
                                    }}
                                />

                                {#if accountEventObj.isRepeating}
                                    <div
                                        class="flex items-center gap-x-2 cursor-pointer"
                                        on:click={openRecurrenceModalOpen}
                                    >
                                        <i class={editIcon} />
                                        <p>Edit</p>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {/if}
                </div>

                {#if accountEventObj.eventType == 1}
                    <div
                        class="flex flex-row w-full justify-start gap-4 items-end"
                    >
                        <div
                            class="flex flex-1 gap-x-4 items-center"
                            use:mapSelectionFromEventData
                        >
                            <p class="w-[120px]">Hospital:</p>
                            <!-- width set to prevent long options go too wide -->
                            <div class="flex w-[calc(100%-120px)]">
                                <Select
                                    bind:value={selectedHospital}
                                    class="flex flex-1 max-h-[35px] rounded-full"
                                    loadOptions={loadHospitals}
                                    items={hospitalOptions}
                                    {groupBy}
                                    itemId="id"
                                    label="name"
                                    on:clear={clearHospital}                                   
                                    noOptionsMessage="No hospital found"
                                    placeholder="Hospital"
                                    on:focus={loadSuggestedHospitals}
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        class="flex flex-row w-full justify-start gap-4 items-end"
                    >
                        <div class="flex flex-1 gap-x-4 items-center">
                            <p class="w-[120px]">Surgeon:</p>
                            <!-- width set to prevent long options go too wide -->
                            <div class="flex w-[calc(100%-120px)]">
                                <Select
                                    bind:value={selectedSurgeon}
                                    id="eventSurgeonSelect"
                                    class="flex flex-1 max-h-[35px] rounded-full"
                                    items={surgeonOptions}
                                    itemId="matchingId"
                                    label="displayLabel"
                                    {groupBy}
                                    on:clear={clearSurgeon}
                                    on:focus={loadSuggestedAccountDoctor}
                                    noOptionsMessage="No surgeon/referrer found"
                                    placeholder="Surgeon/Referrer"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-row w-full justify-start items-end">
                        <div class="flex flex-1">
                            <p
                                class="underline cursor-pointer text-secondary"
                                on:click={openCreateNewSurgeonModal}
                            >
                                Add new surgeon/referrer
                            </p>
                        </div>
                    </div>
                {/if}

                {#if accountEventObj.eventType == 1 && nestedEncounter?.length}
                    <div class="flex gap-x-4 items-center">
                        <p>Nested encounter dates will adjust accordingly</p>
                    </div>
                {/if}
            </div>

            <div class="flex flex-1 flex-col gap-2">
                <p class="w-[120px]">Description:</p>

                <div class="flex flex-1 w-full">
                    <textarea
                        class="textarea textarea-bordered w-full"
                        rows="4"
                        autocomplete="off"
                        bind:value={accountEventObj.description}
                    />
                </div>
            </div>

            {#if !isCreateEvent}
                <div class="flex flex-1 flex-col gap-4 py-3">
                  <!--   <NotesTableWithEdit
                        relTo={NoteTypes.AccountCalendarEvent}
                        relId={accountEventObj.id}
                        title="Event Notes"
                    /> -->
                    <Notes
                        noteOwnerID={accountEventObj.id}
                        noteType={NoteTypes.AccountCalendarEvent}
                        sectionTitle="Event Notes"
                        onFinishModifyNote={() => {}}
                    />
                </div>

                <div class="flex flex-1 flex-col gap-4 py-3">
                    <EventFiles
                        doctorId={accountEventObj.doctorId}
                        relId={accountEventObj.id}
                        title="Event Files"
                        {allFiles}
                    />
                </div>
            {/if}
        {/if}
       
    </div>
</NotificationModal>

{#if isCalendarRecurrenceModalOpen}
    <CalendarRecurrenceModal
        bind:isModalOpen={isCalendarRecurrenceModalOpen}
        bind:eventObj={accountEventObj}
    />
{/if}

<NotificationModal
    isModalOpen={isDeleteEventModalOpen}
    title="Delete Event"
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    cancelText={isDeletingEvent ? "Deleting" : "Delete"}
    isConfirming={isDeletingEvent}
    confirmAction={closeDeleteEventModal}
    closeModal={deleteEvent}
    isCancelDisabled={!deleteType &&
        (accountEventObj.isRepeating || wasEventRepeating)}
    isReverseBtns={true}
    zIndex="1001"
    closeModalFunction={closeDeleteEventModal}
>
    {#if accountEventObj.isRepeating || wasEventRepeating}
        <p class="leading-6 whitespace-pre-line mb-4">Do you want to delete:</p>
        <div class="flex flex-col gap-y-4 font-medium">
            <label class="flex items-center gap-4">
                <input
                    type="radio"
                    class="radio radio-secondary"
                    value={DeleteRepeatingEventOptions.thisOnly}
                    bind:group={deleteType}
                />
                This event only
            </label>
            <label class="flex items-center gap-4">
                <input
                    type="radio"
                    class="radio radio-secondary"
                    value={DeleteRepeatingEventOptions.thisAndFollowing}
                    bind:group={deleteType}
                />
                This and following events
            </label>
            <label class="flex items-center gap-4">
                <input
                    type="radio"
                    class="radio radio-secondary"
                    value={DeleteRepeatingEventOptions.allEvents}
                    bind:group={deleteType}
                />
                All events
            </label>
        </div>
    {:else}
        Do you want to delete this event?
    {/if}
</NotificationModal>

{#if isCreateNewSurgeonModalOpen}
    <CreateSurgeonModal
        {isCreateNewSurgeonModalOpen}
        reloadSurgeonDetails={() =>
            loadAccountDoctors(accountEventObj.doctorId, false)}
        closeFunc={closeCreateNewSurgeonModal}
        bind:selectedSurgeon
        doctorId={accountEventObj.doctorId}
        existedOptions={surgeonOptions}
    />
{/if}

{#if isCalendarRecurrenceEditOptionModalOpen}
    <CalendarRecurrenceEditOptionModal
        {accountEventObj}
        bind:isModalOpen={isCalendarRecurrenceEditOptionModalOpen}
        on:proceedAction={handleModifyEvent}
        {wasEventRepeating}
    />
{/if}
